import { type Address, type Chain, http } from "viem";
import {
  arbitrum,
  arbitrumSepolia,
  avalanche,
  avalancheFuji,
  base,
  baseSepolia,
  bsc,
  bscTestnet,
  coreDao,
  fraxtal,
  fraxtalTestnet,
  gnosisChiado,
  immutableZkEvm,
  katana,
  mainnet,
  mantle,
  mantleTestnet,
  monad,
  monadTestnet,
  plumeMainnet,
  polygon,
  polygonAmoy,
  sepolia,
  sonic,
} from "viem/chains";

import { getRpcApiPath } from "@/lib/utils/get-base-url";
import {
  isValidEthereumAddress,
  isValidInjectiveAddress,
  isValidSolanaAddress,
  isValidSuiAddress,
} from "@/lib/utils/is-true";

import { isTestnetsEnabled } from "../flags";
import { ALL_BLOCKED_CHAINS, CHAIN_SUPPORT_LIST, type ProductFeatures } from "./chain-support-list";
import { EVM_CHAIN_CONFIGS, EVM_SUPPORTED_CHAIN_IDS } from "./evm-chain-details";
import {
  INJECTIVE_CHAIN_CONFIGS,
  INJECTIVE_SUPPORTED_CHAIN_IDS,
  NON_EVM_CHAIN_CONFIGS,
  SUI_CHAIN_CONFIGS,
  SUI_SUPPORTED_CHAIN_IDS,
  SVM_CHAIN_CONFIGS,
  SVM_SUPPORTED_CHAIN_IDS,
  solana,
  sui,
} from "./other-chain-details";
import type { ChainConfig, ChainDisplayInfo, ChainFeature, VirtualMachineConfig, VirtualMachineSlug } from "./types";

const ALL_CHAIN_CONFIGS = {
  ...EVM_CHAIN_CONFIGS,
  ...NON_EVM_CHAIN_CONFIGS,
};
const ALL_CHAIN_CONFIGS_ARR = Object.values(ALL_CHAIN_CONFIGS);

// ============================================================================
// Chain Name Mapping (Prisma to Chain ID)
// ============================================================================

/**
 * Maps Prisma data warehouse chain enum names to viem chain IDs
 * Used for converting chain names from the database to chain IDs for icon rendering
 */
const CHAIN_NAME_TO_ID_MAP: Record<string, number> = {
  arbitrum: arbitrum.id,
  arbitrumSepolia: arbitrumSepolia.id,
  avalanche: avalanche.id,
  avalancheFuji: avalancheFuji.id,
  base: base.id,
  baseSepolia: baseSepolia.id,
  binanceSmartChain: bsc.id,
  binanceSmartChainTestnet: bscTestnet.id,
  core: coreDao.id,
  ethereum: mainnet.id,
  ethereumSepolia: sepolia.id,
  fraxtal: fraxtal.id,
  fraxtalTestnet: fraxtalTestnet.id,
  gnosisChiado: gnosisChiado.id,
  immutable: immutableZkEvm.id,
  injective: 1776,
  injectiveTestnet: 1439,
  katana: katana.id,
  katanaBokuto: 129399,
  mantle: mantle.id,
  mantleTestnet: mantleTestnet.id,
  monad: monad.id,
  monadTestnet: monadTestnet.id,
  plume: plumeMainnet.id,
  polygonAmoy: polygonAmoy.id,
  polygonPos: polygon.id,
  solana: solana.id,
  solanaDevnet: 901,
  solanaTestnet: 902,
  sonic: sonic.id,
  sui: sui.id,
};

/**
 * Converts a Prisma chain name to a viem chain ID
 * @param chainName - The chain name from Prisma (e.g., "ethereum", "arbitrum")
 * @returns The chain ID, or null if not found
 */
export function getChainIdFromChainName(chainName: string): number | null {
  return CHAIN_NAME_TO_ID_MAP[chainName] ?? null;
}

/**
 * Reverse mapping: chain ID to Prisma chain name
 * Used for converting chain IDs to v3 API chain names
 */
const CHAIN_ID_TO_NAME_MAP: Record<number, string> = Object.entries(CHAIN_NAME_TO_ID_MAP).reduce(
  (acc, [name, id]) => ({ ...acc, [id]: name }),
  {} as Record<number, string>,
);

/**
 * Converts a viem chain ID to a v3 API chain name
 * @param chainId - The chain ID (e.g., 1, 42161)
 * @returns The chain name for v3 API, or null if not found
 */
export function getChainNameFromChainId(chainId: number): string | null {
  return CHAIN_ID_TO_NAME_MAP[chainId] ?? null;
}

// ============================================================================
// Generated Types and Constants
// ============================================================================

// Generate supported chain ID type from the configuration
export type SupportedChainId = keyof typeof EVM_CHAIN_CONFIGS | keyof typeof NON_EVM_CHAIN_CONFIGS;

// Get all EVM chain IDs
const ALL_EVM_CHAIN_IDS = Object.keys(EVM_CHAIN_CONFIGS).map(Number) as SupportedChainId[];

// Get all non-EVM chain IDs
const ALL_NON_EVM_CHAIN_IDS = Object.keys(NON_EVM_CHAIN_CONFIGS).map(Number) as SupportedChainId[];

// ============================================================================
// RPC Configuration Helpers
// ============================================================================

// Get chain configuration
export function getChainConfig(chainId: SupportedChainId): Chain {
  const config = ALL_CHAIN_CONFIGS[chainId];
  if (!config) {
    throw new Error(`Unsupported chain: ${chainId}`);
  }
  return config.chain;
}

// Get chain details
export function getChainDetails(chainId: SupportedChainId): ChainConfig {
  const config = ALL_CHAIN_CONFIGS[chainId];
  if (!config) {
    throw new Error(`Unsupported chain: ${chainId}`);
  }
  return config;
}

// Get feature configuration for a chain
function getChainFeature<T extends ChainFeature>(
  chainId: SupportedChainId,
  feature: T,
): NonNullable<ChainConfig["features"][T]> | null {
  const config = ALL_CHAIN_CONFIGS[chainId];
  return (config?.features[feature] as NonNullable<ChainConfig["features"][T]>) || null;
}

// ============================================================================
// Chain Icon Helpers
// ============================================================================
export function getChainIconUrl(chainId: SupportedChainId): string {
  const config = ALL_CHAIN_CONFIGS[chainId];
  const iconSlug = config?.icon || "unknown.svg";
  return `/chain-icons/${iconSlug}`;
}

// ============================================================================
// Feature-specific getters
// ============================================================================

export function getChainInfoFromChainId(chainId: number | undefined): ChainDisplayInfo {
  function getValidationFunction(chainId: SupportedChainId): ((address: string) => boolean) | null {
    const chain = ALL_CHAIN_CONFIGS[chainId];
    return chain.validationFunc ?? null;
  }

  if (!chainId || !isSupportedChainId(chainId)) {
    return {
      exists: false,
      icon: null,
      pretty: null,
      validationFunc: null,
    };
  }

  return {
    exists: true,
    icon: getChainIconUrl(chainId),
    pretty: getChainDetails(chainId)?.prettyName ?? getChainConfig(chainId)?.name,
    validationFunc: getValidationFunction(chainId),
  };
}

// ============================================================================
// Chain Sort / Priority
// ============================================================================

const CHAIN_SORT_RANK: Record<number, number> = {
  [mainnet.id]: 1,
  [monad.id]: 2,
  [avalanche.id]: 3,
  [polygon.id]: 4,
  [solana.id]: 5,
};

type ChainTier = 1 | 2 | 3;

/**
 * Returns a comparator for .sort() that orders chains by strategic priority
 * (Ethereum, Monad, Avalanche, Polygon, Solana), then unranked chains by pretty
 * name, then blocked chains last (also by pretty name among themselves).
 *
 * Pass an accessor that extracts the chainId from each item.
 */
export function getChainComparator<T>(getChainId: (item: T) => number): (a: T, b: T) => number {
  return (a, b) => {
    const aId = getChainId(a);
    const bId = getChainId(b);
    const aTier: ChainTier = aId in CHAIN_SORT_RANK ? 1 : ALL_BLOCKED_CHAINS.includes(aId) ? 3 : 2;
    const bTier: ChainTier = bId in CHAIN_SORT_RANK ? 1 : ALL_BLOCKED_CHAINS.includes(bId) ? 3 : 2;

    if (aTier !== bTier) {
      return aTier - bTier;
    }

    if (aTier === 1) {
      return CHAIN_SORT_RANK[aId] - CHAIN_SORT_RANK[bId];
    }

    const aName = getChainInfoFromChainId(aId).pretty ?? "";
    const bName = getChainInfoFromChainId(bId).pretty ?? "";
    return aName.localeCompare(bName);
  };
}

// ============================================================================
// Fireblocks Integration Helpers
// ============================================================================
/**
 * Get the Fireblocks asset ID for a given chain
 * @param chainId - The chain ID to get the Fireblocks asset ID for
 * @returns The Fireblocks asset ID or null if not supported
 */
export function getFireblocksAssetId(chainId: SupportedChainId): string | null {
  const fireblocksConfig = getChainFeature(chainId, "fireblocks");
  return fireblocksConfig?.assetId || null;
}

// ============================================================================
// Helper Functions
// ============================================================================

export function getExplorerAddressUrl(chainId: SupportedChainId, address: string): null | string {
  const explorerConfig = getChainFeature(chainId, "explorer");
  if (!explorerConfig?.txUrlBase) return null;

  // Convert tx URL to address URL by replacing /tx/ with /address/
  const addressUrlBase = explorerConfig.txUrlBase.replace("/tx/", "/address/");
  return `${addressUrlBase}${address}`;
}

// ============================================================================
// Explorer Helpers
// ============================================================================
export function getExplorerTxUrl(chainId: SupportedChainId, txHash: string): null | string {
  const explorerConfig = getChainFeature(chainId, "explorer");
  return explorerConfig ? `${explorerConfig.txUrlBase}${txHash}` : null;
}

/**
 * Get the RPC endpoint for a chain, supporting both Alchemy and custom endpoints
 * @param chainId - The chain ID to get the RPC endpoint for
 * @param apiKey - The Alchemy API key (only used if chain uses Alchemy)
 * @returns The RPC endpoint URL or null if no RPC is configured
 */
function getRpcEndpoint(chainId: SupportedChainId, apiKey: string): null | string {
  const rpcConfig = getChainFeature(chainId, "rpc");
  if (!rpcConfig) return null;

  // Prioritize custom endpoint over Alchemy
  if (rpcConfig.customEndpoint) {
    return rpcConfig.customEndpoint;
  }

  if (rpcConfig.alchemyEndpoint) {
    return rpcConfig.alchemyEndpoint.replace("${ALCHEMY_API_KEY}", apiKey);
  }

  return null;
}

export function getRpcEndpoints(apiKey: string): Record<number, string> {
  const endpoints: Record<number, string> = {};

  for (const chain of getSupportedEvmChainsWithRpc()) {
    const endpoint = getRpcEndpoint(chain.chain.id, apiKey);
    if (endpoint) {
      endpoints[chain.chain.id] = endpoint;
    }
  }

  return endpoints;
}

// ============================================================================
// Chain Support List Helpers
// ============================================================================
/**
 * Get the list of chain IDs that support a given product feature or any of the given features
 * @param features - The product feature(s) to get the supported chain IDs for
 * @returns The list of chain IDs that support the given product feature(s)
 */
export function getSupportedChainIds(features: ProductFeatures | ProductFeatures[]) {
  if (Array.isArray(features)) {
    // Get unique chain IDs that support at least one of the features
    const chainIdSet = new Set<number>();
    features.forEach((feature) => {
      CHAIN_SUPPORT_LIST[feature].forEach((chainId) => chainIdSet.add(chainId));
    });
    return Array.from(chainIdSet);
  }
  return CHAIN_SUPPORT_LIST[features];
}

// Get chains based on testnet flag
function getSupportedEVMChainIds(): SupportedChainId[] {
  return ALL_EVM_CHAIN_IDS.filter((chainId) => {
    const config = EVM_CHAIN_CONFIGS[chainId];
    return isTestnetsEnabled() || !config.isTestnet;
  });
}

// ============================================================================
// Cross-Chain Feature Helpers
// ============================================================================
// These functions will be implemented when new bridging solution is added

// Get supported chains based on testnet flag
function getSupportedEvmChains(): [ChainConfig, ...ChainConfig[]] {
  const chains = getSupportedEVMChainIds().map((chainId) => EVM_CHAIN_CONFIGS[chainId]);
  if (chains.length === 0) {
    throw new Error("No supported chains configured");
  }
  return [chains[0], ...chains.slice(1)];
}

// Get supported chains that have RPC configurations
export function getSupportedEvmChainsWithRpc(): ChainConfig[] {
  const chains = getSupportedEvmChains().filter((config) => {
    return config.features.rpc !== undefined;
  });
  if (chains.length === 0) {
    throw new Error("No supported chains with RPC configured");
  }
  return chains;
}
// Get supported non-EVM chain IDs
function getSupportedNonEVMChainIds(): SupportedChainId[] {
  return ALL_NON_EVM_CHAIN_IDS.filter((chainId) => {
    const config = NON_EVM_CHAIN_CONFIGS[chainId];
    return isTestnetsEnabled() || !config.isTestnet;
  });
}

// ============================================================================
// Swap Feature Helpers
// ============================================================================
export function getSwapFactoryAddress(chainId: SupportedChainId): Address | null {
  const swapConfig = getChainFeature(chainId, "swap");
  return swapConfig?.factoryProxyAddress || null;
}

// Get all transports
export function getTransports() {
  const transports: Record<number, ReturnType<typeof http>> = {};

  for (const chain of getSupportedEvmChainsWithRpc()) {
    transports[chain.chain.id] = http(getRpcApiPath(chain.chain.id));
  }

  return transports;
}

export function getVirtualMachineSlugFromAddress(networkAddress: null | string): VirtualMachineSlug {
  if (!networkAddress) {
    return "unknown";
  }

  if (isValidEthereumAddress(networkAddress)) {
    return "evm";
  }

  if (isValidSolanaAddress(networkAddress)) {
    return "svm";
  }

  if (isValidSuiAddress(networkAddress)) {
    return "sui";
  }

  // a subset of injective addresses can be overlapped with solana addresses
  // atm we don't support adding injective addresses to the dashboard, so prioritize solana over injective
  if (isValidInjectiveAddress(networkAddress)) {
    return "inj";
  }

  return "unknown";
}

export function isChainSupportRemovedBySlug(slug: VirtualMachineSlug): boolean {
  if (slug === "unknown") {
    return false;
  }
  const vmConfig = getVirtualMachineConfigFromSlug(slug);
  return ALL_BLOCKED_CHAINS.some((chainId) => chainId === vmConfig.nativeCurrencyChainId);
}

/**
 * Check which chains are supported for a given product feature or features.
 * Useful to pass into .filter() functions within feature areas of the app to remove chains that are not supported.
 * @param chainId - The chain ID to check
 * @param features - The product feature(s) to check
 * @returns True if the chain supports the given product feature(s), false otherwise
 */
export function isChainSupported(chainId: number, features: ProductFeatures | ProductFeatures[]): boolean {
  return getSupportedChainIds(features).includes(chainId);
}

// Check if chain ID is supported
export function isSupportedChainId(id: number): id is SupportedChainId {
  return (
    id in EVM_CHAIN_CONFIGS ||
    id in NON_EVM_CHAIN_CONFIGS ||
    getSupportedEVMChainIds().includes(id as SupportedChainId) ||
    getSupportedNonEVMChainIds().includes(id as SupportedChainId)
  );
}

// Check if a chain is a testnet
export function isTestnetChain(chainId: SupportedChainId): boolean {
  const config = ALL_CHAIN_CONFIGS[chainId];
  return config?.isTestnet ?? false;
}

function getChainIDsByExcludingChainConfigs(chainConfigs: Record<number, ChainConfig>): number[] {
  return ALL_CHAIN_CONFIGS_ARR.filter((config) => !(config.chain.id in chainConfigs)).map((config) => config.chain.id);
}

const NON_EVM_CHAIN_IDS = getChainIDsByExcludingChainConfigs(EVM_CHAIN_CONFIGS);
const NON_SVM_CHAIN_IDS = getChainIDsByExcludingChainConfigs(SVM_CHAIN_CONFIGS);
const NON_SUI_CHAIN_IDS = getChainIDsByExcludingChainConfigs(SUI_CHAIN_CONFIGS);
const NON_INJECTIVE_CHAIN_IDS = getChainIDsByExcludingChainConfigs(INJECTIVE_CHAIN_CONFIGS);

export function filterOutTestnetsFromChainIds(chainIds: number[]) {
  return chainIds
    .map((chainId) => getChainDetails(chainId))
    .filter((chain) => !chain.isTestnet)
    .map((chainConfig) => chainConfig.chain.id);
}

export function getUnsupportedNetworksBasedOnAddress(networkAddress: null | string): number[] {
  const addressVirtualMachine = getVirtualMachineSlugFromAddress(networkAddress);

  const CHAIN_IDS = (() => {
    switch (addressVirtualMachine) {
      case "evm":
        return NON_EVM_CHAIN_IDS;
      case "svm":
        return NON_SVM_CHAIN_IDS;
      case "sui":
        return NON_SUI_CHAIN_IDS;
      case "inj":
        return NON_INJECTIVE_CHAIN_IDS;
      case "unknown":
        return [];
      default:
        // ts exhaustiveness checking
        throw new Error(`Unsupported virtual machine slug: ${addressVirtualMachine satisfies never}`);
    }
  })();

  return CHAIN_IDS;
}

export function getVirtualMachineConfigFromAddress(networkAddress: null | string): VirtualMachineConfig {
  const vmSlug = getVirtualMachineSlugFromAddress(networkAddress);

  return getVirtualMachineConfigFromSlug(vmSlug);
}

function getVirtualMachineConfigFromSlug(virtualMachineSlug: VirtualMachineSlug): VirtualMachineConfig {
  switch (virtualMachineSlug) {
    case "evm":
      return {
        compatibleVerbiage: "EVM",
        compatibleVerbiageWithNounPrefix: "an EVM",

        nativeCurrencyChainId: 1,
        nativeCurrencyIconUrl: "/chain-icons/ethereum.svg",
        nativeCurrencySymbol: "ETH",

        prettyName: "Ethereum",
        prettyNameWithNounPrefix: "an Ethereum",

        shortName: "eth",
        slug: "evm",
      };

    case "sui":
      return {
        compatibleVerbiage: "Sui",
        compatibleVerbiageWithNounPrefix: "a Sui",

        nativeCurrencyChainId: 101,
        nativeCurrencyIconUrl: "/chain-icons/sui.svg",
        nativeCurrencySymbol: "SUI",

        prettyName: "Sui",
        prettyNameWithNounPrefix: "a Sui",

        shortName: "sui",
        slug: "sui",
      };

    case "svm":
      return {
        compatibleVerbiage: "Solana",
        compatibleVerbiageWithNounPrefix: "a Solana",

        nativeCurrencyChainId: 900,
        nativeCurrencyIconUrl: "/chain-icons/solana.svg",
        nativeCurrencySymbol: "SOL",

        prettyName: "Solana",
        prettyNameWithNounPrefix: "a Solana",

        shortName: "solana",
        slug: "svm",
      };

    case "inj":
      return {
        compatibleVerbiage: "Injective",
        compatibleVerbiageWithNounPrefix: "an Injective",

        nativeCurrencyChainId: 1776,
        nativeCurrencyIconUrl: "/chain-icons/injective.svg",
        nativeCurrencySymbol: "INJ",

        prettyName: "Injective",
        prettyNameWithNounPrefix: "an Injective",

        shortName: "injective",
        slug: "inj",
      };
    case "unknown":
      return {
        compatibleVerbiage: "Unknown",
        compatibleVerbiageWithNounPrefix: "an Unknown",

        nativeCurrencyChainId: 9999999999,
        nativeCurrencyIconUrl: "/chain-icons/fallback.svg",
        nativeCurrencySymbol: "N/A",

        prettyName: "Unknown",
        prettyNameWithNounPrefix: "an Unknown",

        shortName: "n/a",
        slug: "unknown",
      };
    default:
      // ts exhaustiveness checking
      throw new Error(`Unsupported virtual machine slug: ${virtualMachineSlug satisfies never}`);
  }
}

export function getVirtualMachineSpecificFeatureSpecificSupportedChainIds({
  feature,
  includeOnlyTheseIds,
  virtualMachineSlug,
}: {
  feature: ProductFeatures;
  includeOnlyTheseIds?: number[];
  virtualMachineSlug: VirtualMachineSlug;
}): number[] {
  const allVirtualMachineSupportedChainIds = getVirtualMachineSpecificGloballySupportedChainIds({
    includeOnlyTheseIds,
    virtualMachineSlug,
  });

  const allFeatureSupportedChainIds = getSupportedChainIds(feature);

  const supportedChainIds = allVirtualMachineSupportedChainIds.filter((id) => allFeatureSupportedChainIds.includes(id));

  return supportedChainIds;
}

function getVirtualMachineSpecificGloballySupportedChainIds({
  includeOnlyTheseIds,
  virtualMachineSlug,
}: {
  includeOnlyTheseIds?: number[];
  virtualMachineSlug: VirtualMachineSlug;
}): number[] {
  let globallySupportedChainIds: number[] = [];

  switch (virtualMachineSlug) {
    case "evm":
      globallySupportedChainIds = EVM_SUPPORTED_CHAIN_IDS;
      break;
    case "svm":
      globallySupportedChainIds = SVM_SUPPORTED_CHAIN_IDS;
      break;
    case "sui":
      globallySupportedChainIds = SUI_SUPPORTED_CHAIN_IDS;
      break;
    case "inj":
      globallySupportedChainIds = INJECTIVE_SUPPORTED_CHAIN_IDS;
      break;
    case "unknown":
      break;
    default:
      throw new Error(`Unsupported virtual machine slug: ${virtualMachineSlug satisfies never}`);
  }

  if (includeOnlyTheseIds) {
    return globallySupportedChainIds.filter((id) => includeOnlyTheseIds.includes(id));
  }

  return globallySupportedChainIds;
}
